/**
 * The organisation sign-in's OIDC client (TASK-0064 §3).
 *
 * The SPA is the **public** OIDC client: it drives Authorization Code + PKCE (`S256`) against the organisation authority
 * through `@axa-fr/oidc-client`, which discovers every endpoint from the authority's `.well-known/openid-configuration`,
 * mints the state, the nonce and the code verifier, and validates the returned ID token (signature against the discovered
 * JWKS, issuer, audience, nonce). No client secret exists anywhere in this flow — the client id is public by design.
 *
 * Two properties are load-bearing:
 *
 * - **the configuration comes from the backend** (`GET /auth/organisation/client`), so the SPA requests exactly the scopes
 *   the deployment configured and returns to exactly the callback URI that was registered — never a value this file owns;
 * - **the tokens are handed to the backend and then dropped**: the Prompt Forge session is the backend's (refresh cookie +
 *   in-memory access token), so the IdP tokens are cleared from storage as soon as the session is established, and no
 *   `offline_access` is ever requested.
 */

import { getFetchDefault, OidcClient, TokenAutomaticRenewMode } from '@axa-fr/oidc-client';

import type { OrganisationClientConfig } from '@/types/api';

/** Name the OIDC client is registered under, shared by the sign-in button and the callback page. */
export const ORGANISATION_OIDC_NAME = 'organisation';

/** The SPA route the authority returns the browser to; must match the registered callback URI's path. */
export const ORGANISATION_CALLBACK_ROUTE = '/auth/organisation/callback';

/**
 * Builds (or returns the existing) OIDC client for the organisation flow.
 *
 * `getOrCreate` keys the instance by name, so the button and the callback page share one client and the login state — the
 * stored callback path, the state, the nonce and the code verifier — survive the navigation between them. The
 * configuration must be the same on both sides, which is why it is always read from the backend's cached policy answer.
 *
 * @param config - The public client configuration the backend published.
 * @returns The OIDC client for the organisation flow.
 */
export function createOrganisationOidcClient(config: OrganisationClientConfig): OidcClient {
  return OidcClient.getOrCreate(() => getFetchDefault())(
    {
      client_id: config.clientId,
      redirect_uri: config.redirectUri,
      scope: config.scopes.join(' '),
      authority: config.authority,
      // No silent renew and no service worker: the IdP tokens are exchanged for a Prompt Forge session immediately after
      // the callback, so there is nothing to keep alive and nothing to renew.
      token_automatic_renew_mode: TokenAutomaticRenewMode.AutomaticOnlyWhenFetchExecuted,
    },
    ORGANISATION_OIDC_NAME,
  );
}
